<script setup>
import { useBraindateLink } from '@/composables/useBraindateLink';
import { getConferenceTimezone, getConferenceTimezoneLabel, isArchivedSession, isSessionOnDemand } from '~~/shared/utilities/schedule';
import { useScheduleControls } from '@/composables/useScheduleControls';
import { useScheduleAction } from '@/composables/useScheduleAction';

const dayjs = useDayjs();
// Global feature gates (client-side, time-released) for My Schedule / Save a Seat.
const { showMySchedule, showSeatReservations } = useScheduleControls();

const props = defineProps({
    session: { type: Object, required: true },
    actionRequired: { type: Boolean, default: false },
    hasSavedSeat: { type: Boolean, default: false },
    isFull: { type: Boolean, default: false },
    year: { type: Number, default: 2026 },
    // Dark theme — recolors the card for placement on the purple session-detail
    // background (Related Sessions), per Nick's Figma.
    onDark: { type: Boolean, default: false },
    // ─── Find a Session suggestion card reuse (2026-07-10) ───────────────────
    // All three default OFF so every existing agenda consumer is byte-for-byte
    // unchanged. Set only by the Find a Session modal
    // (fragments/dialogs/find-a-session.vue), which renders this same card for
    // its "We think you'd like this" suggestions.
    //
    // recommendation: when non-empty, render the AI reason callout between the
    // description and the speaker row.
    recommendation: { type: String, default: '' },
    // delegateAdd: hand the whole add flow to the parent. The Add button emits
    // `add` and runs NONE of this card's own logic (no enqueue/refresh/
    // schedule:changed/seat-reminder/remove-toggle) — the modal owns its
    // gap-mode-closes / all-mode-queues behavior. The button's saved state
    // still derives from user.scheduled, so it flips to "Added to My Schedule"
    // once the parent's add + refreshSession lands.
    delegateAdd: { type: Boolean, default: false },
});

const emit = defineEmits(['add']);

// Resolve NuxtLink to the component OBJECT up front and hand that to the
// title/description `<component :is>` below. Passing the bare string
// 'nuxt-link' to a dynamic `:is` fails to resolve inside the Find a Session
// modal's overlay render context (it renders a dead <nuxt-link> custom
// element that never navigates / never opens the session drawer); the object
// form bypasses string resolution and works in every mount context, agenda or
// overlay. (Literal `<nuxt-link>` tags — e.g. the speaker links — are
// unaffected and stay as-is.)
const NuxtLink = resolveComponent('NuxtLink');

// Archived (past-year) sessions have no reservation state — they should never
// show date/time/location or a Limited Capacity / Full header. Matches
// compact-session-card / session header.

// The on-demand glyph. `on_demand` is Sessionboard's checkbox (UNB-753 era
// follow-up); `livestream` is the legacy flag, true on exactly 37 archived 2025
// rows and on nothing in the 2026 catalog. OR-ed rather than replaced so the
// archive keeps the icon it already shows.
const isOnDemand = computed(() => isSessionOnDemand(props.session));

const isArchive = computed(() => isArchivedSession(props.session));

// "Save a seat" is the only action the orange treatment ever asks for, so it
// can't be action-*required* before seat saving opens. Gated on the same
// time-release as the Save a Seat button itself, otherwise every limited-capacity
// session on My Schedule reads as orange/urgent months early.
const seatActionRequired = computed(() => props.actionRequired && showSeatReservations.value);

// A clash the attendee has to resolve, set client-side by agenda.vue's
// applyConflicts overlay (the shared feed can't carry it). A MODIFIER rather
// than a variant: the card keeps its own body colour and buttons, and only gains
// the red header/border, the "!" and an appended " - ACTION NEEDED"
// (Jack, 2026-08-20).
const isConflicted = computed(() => props.session.is_conflicted === true);

const variant = computed(() => {
    if (props.session.is_my_event || props.session.is_external) return 'user-event';
    if (isArchive.value) return 'generic';
    // Full + saved seat → "everything done"
    if (props.hasSavedSeat && props.isFull && props.session.session_reservations === true) return 'full-saved-seat';
    // Full + added + no seat → "you got in but need to grab a seat"
    if (seatActionRequired.value && props.isFull && props.session.session_reservations === true) return 'full-action-required';
    // Limited (not full) + added + no seat. This case was missing here, so a
    // session the attendee had already added read as a plain 'limited-capacity'
    // card in Detail while Overview and Calendar showed it as action-required.
    if (seatActionRequired.value) return 'action-required';
    if (props.session.session_reservations === true && props.isFull) return 'full-session';
    if (props.session.session_reservations === true) return 'limited-capacity';
    return 'generic';
});

const cardClasses = computed(() => {
    // Hovering the card itself only raises the purple glow shadow, matching the
    // Figma "Card Drop Shadow" effect (shared across views). Title, description
    // and speaker images each own their hover state — deliberately not a `group`,
    // so hovering the card background doesn't fire all of them at once.
    const base = '@container relative flex flex-col items-start overflow-clip rounded-[10px] w-full h-full pb-[26px] lg:pb-[40px] transition-shadow duration-200 hover:shadow-[0_0_8px_0_rgba(130,12,167,0.5)]';
    const body = props.onDark ? ({
        'action-required':      'bg-[#d63d00]',
        'full-action-required': 'bg-[#d63d00]',
        'full-session':         'bg-(--purple)',
    }[variant.value] || 'bg-[#9800c8]') : ({
        'full-session':         'bg-(--pink)',
        'action-required':      'bg-(--orange)',
        'full-action-required': 'bg-(--orange)',
    }[variant.value] || 'bg-white');

    // Replaces the base border rather than adding to it: two border-colour
    // utilities in one class list leaves the winner to CSS source order.
    // (onDark never carries a conflict in practice — is_conflicted is only set on
    // My Schedule, which does not render on-dark — but the rule holds either way.)
    const baseBorder = variant.value === 'full-session'
        ? (props.onDark ? 'border border-[#c138f2]' : 'border border-(--dark-pink)')
        : '';
    const border = isConflicted.value ? 'border border-[#980000]' : baseBorder;

    return `${base} ${body} ${border}`.trim();
});

const hasHeader = computed(() => variant.value !== 'generic' || isConflicted.value);

const headerClasses = computed(() => {
    const base = 'flex items-center gap-[10px] h-[36px] px-[26px] lg:px-[40px] py-[14px] w-full';
    const bgByVariant = props.onDark ? {
        'limited-capacity':     'bg-(--magenta)',
        'action-required':      'bg-(--magenta)',
        'full-session':         'bg-[#a058b6]',
        'full-saved-seat':      'bg-[#a058b6]',
        // --dark-pink (#A55C87), matching what this same card shows on a light
        // ground. The other two full-* variants take #a058b6 here as a
        // deliberate substitution for the purple page; this one was left on
        // --magenta, which reads as near-black against that purple and unlike
        // every other version of the card on the site (Nick, Asana
        // 1217804287639795).
        'full-action-required': 'bg-(--dark-pink)',
        'user-event':           'bg-(--purple)',
    } : {
        'limited-capacity':     'bg-(--magenta)',
        'action-required':      'bg-(--magenta)',
        'full-session':         'bg-(--dark-pink)',
        'full-saved-seat':      'bg-(--dark-pink)',
        'full-action-required': 'bg-(--dark-pink)',
        'user-event':           'bg-(--purple)',
    };
    // Error red wins over every variant colour — it is the conflict signal.
    if (isConflicted.value) return `${base} bg-[#980000]`;
    // A braindate takes the Braindate pill's own fill (#af264d, the same literal
    // braindate-card.vue uses — it has no token in variables.scss), so the card
    // reads as the same thing the Braindate block advertises. An event the
    // attendee created keeps the purple.
    if (variant.value === 'user-event' && props.session.is_braindate) return `${base} bg-[#af264d]`;
    const bg = bgByVariant[variant.value];
    return bg ? `${base} ${bg}` : '';
});

const baseHeaderLabel = computed(() => {
    switch (variant.value) {
        case 'limited-capacity':
        // Same label as limited-capacity: the difference is the orange card and
        // the live Save a Seat button, not the wording.
        case 'action-required':      return 'LIMITED CAPACITY';
        case 'full-session':
        case 'full-saved-seat':
        case 'full-action-required':case 'full-action-required': return 'SESSION IS FULL';
        // "Waitlisted", never "Standby" (client, 2026-09-03), reversing the
        // earlier ruling. The Figma's "WAITLISTED" is now the one that matches.
        // A braindate is a My Event synced from Bizzabo; it says so rather than
        // claiming the attendee created it.
        case 'user-event':           return props.session.is_braindate ? 'BRAINDATE' : 'MY EVENT';
        default:                     return '';
    }
});

// A conflict appends rather than replaces, so the card keeps saying what it
// already said. A variant with no header of its own reads as "ACTION NEEDED".
// Standby appends the same way a conflict does, and for the same reason: the
// card keeps saying what it already said. "SESSION IS FULL" is still true — the
// attendee is on the list precisely BECAUSE it is full — so this adds rather
// than replaces. "Waitlisted", never "Standby" (client, 2026-09-03). This REVERSES the
// earlier ruling — the app said Standby everywhere the attendee could read
// it — so the Figma's "WAITLISTED" is now the one that matches.
const standbyHeaderLabel = computed(() => {
    if (!seatButtonWaitlisted.value) return baseHeaderLabel.value;
    // APPENDED to whatever the header already said, not matched against
    // 'SESSION IS FULL'. Bizzabo answers 202 on its own reading of capacity, and
    // `is_full` is the stalest field we hold — bizzabo:capacity writes it every
    // 30 minutes — so an attendee can hold a standby place on a session our data
    // still calls LIMITED CAPACITY. Keying the suffix to our guess left the
    // header silent in exactly that case, while the button beside it was filled.
    return baseHeaderLabel.value ? `${baseHeaderLabel.value} - WAITLISTED` : 'WAITLISTED';
});

const headerLabel = computed(() => {
    if (!isConflicted.value) return standbyHeaderLabel.value;
    return standbyHeaderLabel.value ? `${standbyHeaderLabel.value} - ACTION NEEDED` : 'ACTION NEEDED';
});

// AI reason callout. On a dark card the dark-pink stroke and text don't carry
// enough contrast, so both go #FCD4E5 there — the value Nick specified on the
// ticket for accessibility. The sparkle takes the same colour: it's the stroke
// of the same block, and leaving it dark-pink would strand it against the dark
// background the rest of the callout just moved off.
//
// ORANGE IS TESTED FIRST, and the order is the whole of UNB-747. The text sits
// on the CARD's body, not on the page behind it, so an orange card needs white
// wherever it renders. With onDark first, Related Sessions on a session detail
// page (:on-dark="isPage") put the dark-page #FCD4E5 on an action-required
// card — pale pink on #D63D00, which is what failed contrast.
const reasonBoxClasses = computed(() => {
    // White on the orange cards, same reason as the pills: dark-pink on orange
    // is barely legible.
    if (isOrangeCard.value) return 'border-white';
    if (props.onDark) return 'border-[#FCD4E5]';
    return 'border-(--dark-pink)';
});
const reasonTextClasses = computed(() => {
    if (isOrangeCard.value) return 'text-white';
    if (props.onDark) return 'text-[#FCD4E5]';
    return 'text-(--dark-pink)';
});

// The sparkle sits INSIDE the badge circle, and that circle is drawn in
// currentColor — whatever reasonTextClasses just set. So the glyph has to invert
// against it. #7d4566 is the same inversion the Overview and Calendar cards
// already use for this badge.
//
// Both LIGHT circles need it, not just the orange card's white one: on a purple
// (onDark) card the circle is #FCD4E5, and a white sparkle disappeared into it
// exactly as it did on orange — the badge read as a plain blank dot (Nick,
// Asana 1217736414957670). Only the default card, whose circle is dark pink,
// keeps the white glyph.
const reasonSparkleFill = computed(() => (
    isOrangeCard.value || props.onDark ? '#7d4566' : 'white'
));

const timeClasses = computed(() => {
    // Orange is checked FIRST. An orange card is orange whatever the page behind
    // it is doing, and #fcc3dc — chosen for the dark purple ground — is a pale
    // pink on mid orange that fails contrast. With the onDark arm first, a
    // related session needing action on the dark session page got the unreadable
    // pairing (same report as compact-session-card, Asana 1217802085538896).
    if (variant.value === 'action-required' || variant.value === 'full-action-required') return 'text-white';
    if (props.onDark) return 'text-[#fcc3dc]';
    if (variant.value === 'full-session') return 'text-[#7d4566]';
    return 'text-(--dark-pink)';
});

// Description currently shares the same color — reused intentionally; split if they diverge.
const titleClasses = computed(() => {
    if (props.onDark) return 'text-white';
    if (variant.value === 'action-required' || variant.value === 'full-action-required') return 'text-white';
    return 'text-(--magenta)';
});

const stageBadgeClasses = computed(() => {
    if (props.onDark) return 'border-white text-white';
    if (variant.value === 'action-required' || variant.value === 'full-action-required') return 'border-white text-white';
    if (variant.value === 'full-session') return 'border-[#7d4566] text-[#7d4566]';
    return 'border-(--dark-pink) text-(--dark-pink)';
});

const videoIconClasses = computed(() => {
    if (props.onDark) return 'text-white';
    if (variant.value === 'action-required' || variant.value === 'full-action-required') return 'text-white';
    return 'text-(--magenta)';
});

// Session-type pill — white outline on the dark theme (and on the orange
// full-action card) so it stays legible; dark-pink (#A55C87) outline otherwise.
const typePillClasses = computed(() => {
    if (props.onDark || isOrangeCard.value) {
        return 'border-white text-white hover:bg-white hover:text-(--magenta)';
    }
    return 'border-(--dark-pink) text-(--dark-pink) hover:bg-(--dark-pink) hover:text-white';
});

// hasTwoButtons: limited-capacity and full-action-required show both Add + Seat
const hasTwoButtons = computed(() =>
    variant.value === 'limited-capacity'
    || variant.value === 'action-required'
    || variant.value === 'full-saved-seat'
    || variant.value === 'full-action-required'
    // Full sessions used to drop this button — "a session at capacity cannot be
    // reserved, so Save a Seat is a dead action" (Bri via Marker.io, Asana
    // 1217733984444914; Nick's 1217697012897127). That premise no longer holds:
    // Bizzabo answers a PUT on a full session with 202 and puts the attendee on
    // its standby list, verified against the live API 2026-09-02. Joining
    // standby IS an action now, so the button stays and reads "Join Waitlist".
    || variant.value === 'full-session'
);

const actionsRowClasses = computed(() => {
    // gap-0 let the two buttons touch (UNB-752, Steph via Marker.io). It is not
    // visible while `justify-between` has room to spread them, which is why it
    // survived: it only shows when their combined width fills the row, or when
    // they wrap and stack with nothing between them.
    //
    // 16px per Jack's ruling that button rows are 16 everywhere (2026-08-18).
    // NOTE overview-card uses 27px on the same row — worth reconciling, but not
    // by guessing here.
    const base = 'flex flex-wrap items-start gap-[16px] px-[26px] pt-[26px] lg:px-[40px] lg:pt-[40px] w-full mt-auto';
    return hasTwoButtons.value ? `${base} justify-between` : `${base} justify-start`;
});

// Add/Seat button state + "Add to My Schedule" flow — shared with the other
// agenda cards and the session drawer via useScheduleAction (see that file
// for the enqueue → conflict→doubleBook → refreshSession → emit →
// seatReminder flow).
const { busy, addButtonSaved, seatButtonSaved, seatButtonWaitlisted, handleAdd, handleSeat } = useScheduleAction(
    toRef(props, 'session'),
    { extraSaved: () => variant.value === 'user-event' },
);

// Add-button click: in delegateAdd mode the parent owns everything — emit
// `add` (only when not already saved, so a saved card can't re-add) and run
// none of this card's own mutate/refresh/toggle logic. Otherwise the normal
// agenda handler.
const onAddButton = (e) => {
    if (props.delegateAdd) {
        if (!addButtonSaved.value) emit('add');
        return;
    }
    handleAdd(e); // pass the event so the composable can see Option+Shift (bulk-clear)
};

// BOTH action-required variants paint the card orange, so both need the
// on-orange treatment for everything sitting on it — the outlined pills and
// buttons included. Naming only the full variant here left the plain
// action-required card with dark-pink stroke and dark-pink text on orange.
const isOrangeCard = computed(() =>
    variant.value === 'action-required' || variant.value === 'full-action-required'
);

// Container-query responsive, in two steps rather than one (Nick, UNB-682).
// Measured on the CARD, not the viewport — the same card renders at wildly
// different widths on the same screen (85vw in a carousel, a grid column on the
// agenda), so viewport breakpoints cannot describe this.
//
//   >= 440px   icon + label
//   399-440px  label only — the icon goes first, which buys back the horizontal
//              padding between two adjacent pills instead of letting them close
//              up until they touch
//   < 399px    icon only, as a 34px circle (the Overview card's button shape)
//
// This replaces a single 380px collapse. Related Sessions used to opt out of the
// collapse entirely via a `full-button` prop (UNB-451988/86), which is why its
// 85vw mobile cards were wrapping the Add and Seat pills onto two lines — the
// case that opened this ticket. The rules are universal now and that prop is
// gone; at the carousel's md widths (440/480px) the buttons still read in full.
const buttonBase = computed(() => {
    const base = 'flex items-center justify-center gap-[8px] px-[14px] py-[8px] rounded-[50px] border transition-colors duration-200 font-hubspot-serif text-[15px] leading-[1.2] tracking-[0.3px] whitespace-nowrap';
    return `${base} @max-[399px]:size-[34px] @max-[399px]:p-0 @max-[399px]:gap-0 @max-[399px]:rounded-full`;
});

// Hidden only in the middle band. Two stacked container variants, which compile
// to nested @container rules and therefore AND together — expressing it as a
// max/min pair of sibling rules instead would leave the winner to source order.
//
// Both numbers are MEASURED, not chosen — re-measured on staging at 15px
// HubSpot Serif for UNB-753. Worst RESTING pair is now "Added to My Schedule" +
// "Join Waitlist": 343.5px with icons, 302.5px without. Add the 16px gap and the
// row's padding and the thresholds fall out.
//
// THE PADDING IS 80px, NOT 52px. That is what UNB-753 actually was. The previous
// 401/364 were derived from `px-[26px]` (52px), but every desktop card renders at
// `lg:px-[40px]` — 80px — so both numbers were 28px too low for the layout they
// are mostly seen in. A 412px agenda card cleared the old 401px "keep icons" gate
// while having 28px less room than the arithmetic assumed: the pair needed 334px
// (Save a Seat) to 342px (Join Waitlist) against 332px available, overflowed by
// as little as 2px, and flex-wrap stacked the two pills. That is the screenshot
// on the ticket.
//
// The rename triggered it rather than caused it — "Join Waitlist" measures 135px
// against "Save a Seat" at 127.7px, and those 7.3px pushed an already-marginal
// case over the edge.
//
// Sized to the 80px padding so the row can never wrap at either padding. On
// mobile (52px) this condenses ~28px earlier than strictly necessary, which is
// the safe direction: early condensing looks deliberate, a wrap does not.
//
// The HOVER pair is handled by swapping the copy, not by another threshold.
// "Added to My Schedule" + "Remove from Waitlist" needs a 507.6px card with
// icons; the three-up grid gives 412px. Rather than size the resting layout for
// a transient state — which would force icon-only circles everywhere — the seat
// pill shows "Leave List" (fits from 384.7px) below 508px and the full wording
// above it, where the 600px cards have room. See the seat button markup.
//
// The Add pill needs no equivalent: its hover label "Remove Session" is 164px
// against "Added to My Schedule" at 208.5px, so that side only ever shrinks.
const ICON_BAND = '@min-[399px]:@max-[440px]:hidden';

const hover = computed(() => isOrangeCard.value
    ? 'hover:bg-white hover:border-white hover:text-(--magenta)'
    : 'hover:bg-(--orange) hover:border-(--orange) hover:text-white');

const addButtonClasses = computed(() => {
    if (addButtonSaved.value) {
        return `${buttonBase.value} ${hover.value} bg-(--magenta) border-(--magenta) text-white`;
    }
    if (props.onDark || isOrangeCard.value) {
        return `${buttonBase.value} ${hover.value} bg-transparent border-white text-white`;
    }
    return `${buttonBase.value} ${hover.value} bg-transparent border-(--magenta) text-(--magenta)`;
});

// A full session cannot offer a seat, only a place in the queue — so the seat
// button becomes a standby affordance there, label and icon together. Limited
// (not full) sessions keep "Save a Seat" and the chair glyph.
//
// seatButtonSaved is excluded because a held seat on a now-full session is
// still a held seat: it reads "Seat Saved", not "Join Waitlist".
// props.hasSavedSeat as well as seatButtonSaved: the variant machine decides
// 'full-saved-seat' from the PROP, while seatButtonSaved derives from the user
// session. Reading only one of them let the card claim 'full-saved-seat' in its
// header while the button underneath said "Join Waitlist".
const seatIsStandby = computed(() =>
    seatButtonWaitlisted.value
    || (props.isFull
        && !seatButtonSaved.value
        && !props.hasSavedSeat
        && props.session.session_reservations === true)
);

const seatButtonClasses = computed(() => {
    // A standby place is held too — same removal affordance as a seat.
    if (seatButtonSaved.value || seatButtonWaitlisted.value) {
        return `${buttonBase.value} ${hover.value} bg-(--magenta) border-(--magenta) text-white`;
    }
    if (props.onDark || isOrangeCard.value) {
        return `${buttonBase.value} ${hover.value} bg-transparent border-white text-white`;
    }
    return `${buttonBase.value} ${hover.value} bg-transparent border-(--magenta) text-(--magenta)`;
});

const stageName = computed(() =>
    props.session.locations?.[0]?.name || props.session.location?.[0]?.name || ''
);

const sessionTypeName = computed(() =>
    props.session.session_type?.[0]?.name || ''
);

const formattedDateTime = computed(() => {
    if (!props.session.start_date_and_time) return '';
    const start = dayjs(props.session.start_date_and_time).tz(getConferenceTimezone(props.year));
    const end = props.session.end_date_and_time
        ? dayjs(props.session.end_date_and_time).tz(getConferenceTimezone(props.year))
        : null;
    const dayStr = start.format('ddd');           // "Wed"
    const dateStr = start.format('MMM D');        // "Sep 16"
    const startStr = start.format('h:mma');       // "9:30am"
    const endStr = end ? end.format('h:mma') : '';
    const tzLabel = getConferenceTimezoneLabel(props.year);
    const timeRange = end ? `${startStr} - ${endStr} ${tzLabel}` : `${startStr} ${tzLabel}`;
    // Location/stage is intentionally omitted here — it's already shown in the card
    // header pill, and only belongs on the session detail page/drawer (per Nick).
    // Bullet separators: an nbsp + regular space on each side renders two visible
    // spaces (the nbsp can't collapse) while still allowing the line to wrap.
    const sep = '  •  ';
    return `${dayStr}${sep}${dateStr}${sep}${timeRange}`;
});

const speakers = computed(() => props.session.speakers || []);

// Past five speakers the row collapses to a "+N" hexagon (Nick, Asana
// 1217803465946591; mockups at Figma 6602:240212). This replaces the solved
// overlap that squeezed ten faces into the six-face width: at ten about a third
// of each face showed, and the row still ran off the card on narrow desktops.
//
// The row keeps the designed 10px overlap and shows as many faces as fit, up to
// five, with the chip counting the rest. "As many as fit" is a container query
// on the row's own width rather than a breakpoint: the card is 360px wide at
// 1440, 290 on a 390 phone, and only 221 in the three-up grid at 1024, so no
// viewport width says how much room the row has. Each hexagon adds 54px, so s
// hexagons (faces plus the chip) need 54s + 10 — 172, 226, 280, 334.
//
// Five or fewer is untouched: no chip, all faces, the authored overlap.
const SPEAKER_FACES_MAX = 5;
const collapseSpeakers = computed(() => speakers.value.length > SPEAKER_FACES_MAX);
const shownSpeakers = computed(() => (
    collapseSpeakers.value ? speakers.value.slice(0, SPEAKER_FACES_MAX) : speakers.value
));
// Faces 0 and 1 always show; face i needs room for itself and the chip, i + 2
// hexagons. Literal strings so Tailwind compiles them.
const SPEAKER_FACE_FIT = [
    '', '',
    'hidden @min-[226px]/speakers:flex',
    'hidden @min-[280px]/speakers:flex',
    'hidden @min-[334px]/speakers:flex',
];
// One label per possible face count, each shown only across the widths where
// exactly that many faces fit, so the count always matches what is hidden.
const SPEAKER_CHIP_LABELS = [
    { faces: 2, cls: '@min-[226px]/speakers:hidden' },
    { faces: 3, cls: 'hidden @min-[226px]/speakers:inline @min-[280px]/speakers:hidden' },
    { faces: 4, cls: 'hidden @min-[280px]/speakers:inline @min-[334px]/speakers:hidden' },
    { faces: 5, cls: 'hidden @min-[334px]/speakers:inline' },
];
// The chip is the speaker hexagon filled flat. #FCD4E5 on every body except the
// pink full card, where it would vanish, so it goes white there (Figma).
const speakerChipFill = computed(() => (
    !props.onDark && variant.value === 'full-session' ? '#ffffff' : '#FCD4E5'
));

// Speaker hex path — same shape as /img/2026/speakers/speaker-hex.svg.
// Rendered inline as SVG so we can stroke a 2px white border around it
// (mask-image can't draw a border).
const SPEAKER_HEX_PATH = 'm35.5674 1.01367c1.8852-.120654 3.6771.56919 5.2666 1.39844.802.41843 1.5833.88955 2.3203 1.33887.747.45541 1.4409.88358 2.1103 1.24804h.001c3.5394 1.92827 7.581 3.66608 11.0557 6.10838l.1152.0811.0889.1103c.5136.6378 1.0929 1.3636 1.5293 2.2256l.1777.3779.0293.0674.0195.0713c2.1647 8.0619 4.3687 16.1451 6.4395 24.252.6538 2.5585 1.5184 5.4786 2.0352 8.1425.292 1.5058.5514 3.3714-.6612 5.045-.4147.5723-1.2134 1.1303-2.0215 1.6201-.8558.5187-1.8936 1.0598-2.9404 1.5723-2.069 1.0129-4.3327 1.9888-5.3008 2.4423-6.5139 3.0523-13.4056 6.4051-20.0713 9.1446-3.9674 1.6302-6.867.1976-10.1513-.834-3.8579-1.2116-8.616-2.5963-12.3994-4.1797-1.49-.6236-2.5649-1.4433-3.35355-2.5518-.77247-1.0856-1.22102-2.381-1.57813-3.8603-2.28745-9.4775-4.43693-19.3625-6.39551-28.9199-.0949-.4631-.1979-.9098-.30078-1.3496-.1019-.4357-.20502-.872-.29394-1.292-.17661-.8342-.314995-1.683-.28321-2.5127.03264-.8512.24367-1.6874.75879-2.4844.50791-.7859 1.27147-1.4724 2.31348-2.0977 2.12436-1.2751 4.64477-2.444 6.77245-3.5341 4.3394-2.2242 8.7871-4.3373 13.1152-6.50298.7039-.35222 1.5611-.82458 2.5225-1.35253.9531-.52342 1.9983-1.09543 3.0488-1.62891 1.0491-.53275 2.1214-1.03647 3.1338-1.4209 1.0005-.3799 2.0034-.66729 2.8975-.72461z';

// Unique per-card id so multiple cards on the same page don't collide on
// <pattern> ids. Stable across re-renders.
const cardId = computed(() => `agenda-${props.session.id || props.session.unique_id || 'session'}`);

// Speaker shard stroke matches the card background so the border reads as
// negative space cut into the card — not a contrasting outline.
// Hover underline. The dotted darker-pink is invisible against the orange card
// bodies, so those take white (Nick, [Agenda] [Orange Cards]). Keyed on the
// variant rather than the conflict modifier — a conflict leaves the body colour
// alone, so what the underline sits on is unchanged.
const onOrangeBody = computed(() => (
    variant.value === 'action-required' || variant.value === 'full-action-required'
));
// #7D4566 is a dark plum, which reads on the light and pink bodies and vanishes
// on the purple session page — this card is used there for Related Sessions and
// never learned about that ground. #FCD4E5 is what the rest of the site already
// uses on dark: compact-session-card decorates with it, grid-card colours with
// it (Nick, Asana 1217805175041854).
//
// Orange is checked FIRST, as in timeClasses: an orange card is orange whatever
// is behind it, and white is what Nick asked for there.
const hoverUnderlineCls = computed(() => {
    const base = 'hover:underline hover:decoration-dotted hover:decoration-[6%] hover:underline-offset-[15.5%]';
    if (onOrangeBody.value) return `${base} hover:decoration-white`;
    if (props.onDark) return `${base} hover:decoration-[#FCD4E5]`;
    return `${base} hover:decoration-[#7D4566]`;
});

const speakerStrokeColor = computed(() => {
    // BOTH orange variants, not just the full one: action-required carries the
    // same --orange body, so it was cutting a white shard out of an orange card
    // (Nick, UNB-720). The `seats_needed` filter he reported from shows exactly
    // those cards — limited capacity, added, no seat yet.
    if (props.onDark) {
        switch (variant.value) {
            case 'action-required':
            case 'full-action-required': return '#D63D00'; // --orange
            case 'full-session':         return '#820CA7'; // --purple
            default:                     return '#9800C8'; // light purple
        }
    }
    switch (variant.value) {
        case 'full-session':         return '#FCC3DC'; // --pink
        case 'action-required':
        case 'full-action-required': return '#D63D00'; // --orange
        default:                     return '#ffffff';
    }
});

// Sponsors (primary + also-sponsors) for the logo row. The logo lives at
// sponsor_logo[0].url on the Sessionboard data; fall back to a name chip when
// a sponsor has no logo.
const sponsorList = computed(() => {
    const s = props.session;
    return [...(s.sponsors || []), ...(s.also_sponsors || [])]
        .filter(Boolean)
        .map(sp => ({
            name: sp.name,
            logoUrl: sp.sponsor_logo?.[0]?.url || sp.logo?.[0]?.url || null,
        }));
});

const sessionLink = computed(() =>
    `/sessions/${props.session.unique_id || props.session.id}`
);

const openMyEvent = inject('openMyEvent', null);
const isMyEvent = computed(() => props.session.is_my_event === true);

// A braindate is a My Event the attendee did not create and cannot change: it
// is synced from Bizzabo, and editing it here would be overwritten on the next
// sync while DELETING it would clear their schedule without cancelling the
// actual booking. So it never opens the editor — it goes to Braindate, which is
// the only place the conversation can actually be managed.
//
// Routed through useBraindateLink for the same reasons the Braindate block uses
// it: it appends the SSO pair so the attendee lands signed in, and it validates
// the destination against the configured Braindate origin. That validation is
// not ceremony here — the url is lifted out of HTML that Braindate writes into
// Bizzabo, which is no more ours to trust than a Builder field.
// Deliberately NOT `&& event_url` — being a braindate is what makes it
// read-only, and whether we have a link to send them to is a separate question.
// Tying the two together meant a synced braindate with no permalink fell
// through to the editor, which is the exact thing this prevents.
const isBraindate = computed(() => props.session.is_braindate === true);
const { href: braindateHref } = useBraindateLink(() => props.session.event_url);

const handleCardClick = (e) => {
    if (!isMyEvent.value) return;            // normal sessions keep their nuxt-link
    e.preventDefault();
    if (isBraindate.value) {
        // On My Schedule the viewer is logged in by definition, so the
        // composable's logged-out branch cannot apply and this always resolves.
        if (braindateHref.value) window.open(braindateHref.value, '_blank');
        return;
    }
    openMyEvent?.(props.session._event);
};
</script>

<template>
    <!-- The root here is not a link (unlike overview/calendar cards) — only the
         title and description below navigate. For My Events those two inner
         links become plain divs, and this root click handler (a no-op for
         normal sessions) opens the modal when the click bubbles up. -->
    <div
        :class="[cardClasses, isMyEvent ? 'cursor-pointer' : '']"
        :role="isMyEvent ? 'button' : undefined"
        :tabindex="isMyEvent ? 0 : undefined"
        @click="handleCardClick"
        @keydown.enter="handleCardClick"
        @keydown.space="handleCardClick"
    >
        <!-- Header bar (all variants except generic) -->
        <div v-if="hasHeader" :class="headerClasses">
            <!-- Alert glyph, purple "!" per spec. BOTH action-required variants
                 (UNB-721): Overview and Calendar have always shown it on the
                 plain limited-capacity-needs-a-seat card, and Details showed it
                 only when the session was also full — so the seats_needed view
                 Nick reported from was a page of cards asking for action with
                 nothing marking them. Figma 3627:49136 shows it on both. -->
            <span
                v-if="variant === 'action-required' || variant === 'full-action-required' || isConflicted"
                class="inline-flex items-center justify-center size-[14px] rounded-full bg-white text-(--purple) text-[11px] font-bold leading-none shrink-0"
            >!</span>
            <span class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-white">
                {{ headerLabel }}
            </span>
        </div>

        <!-- Body container -->
        <div class="flex flex-col gap-[27px] items-start pt-[26px] px-[26px] lg:pt-[40px] lg:px-[40px] w-full">
            <!-- Content stack. The gap here is title -> description: 14px on
                 mobile per UNB (Nick, 08-18), the larger desktop value unchanged. -->
            <div class="flex flex-col gap-[14px] lg:gap-[24px] items-start w-full">
                <!-- Title section. The gap here is date/time -> title: 9px on
                     mobile per UNB-702, the larger desktop value unchanged. -->
                <div class="flex flex-col gap-[9px] lg:gap-[16px] items-start w-full">
                    <!-- Header section (stage badge + date/time) -->
                    <div class="flex flex-col gap-[11px] items-start w-full">
                        <!-- Top metadata block -->
                        <div class="flex flex-col gap-[18px] items-start w-full">
                            <!-- Stage badge row (skip for user-event, skip if nothing to show) -->
                            <div
                                v-if="variant !== 'user-event' && (stageName || isOnDemand)"
                                class="flex gap-[14px] items-center w-full"
                                :class="(stageName && isOnDemand) ? 'justify-between' : 'justify-start'"
                            >
                                <!-- Session type badge — clickable to filter agenda by this type -->
                                <button
                                    v-if="sessionTypeName"
                                    type="button"
                                    class="border rounded-[25px] px-[15px] pt-[1px] pb-[4px] shrink-0 bg-transparent cursor-pointer transition-colors duration-150"
                                    :class="typePillClasses"
                                    @click.stop.prevent="navigateTo(`/agenda?session_types=${session.session_type?.[0]?.id}&view=list`)"
                                >
                                    <span class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase">
                                        {{ sessionTypeName }}
                                    </span>
                                </button>

                                <!-- On-demand / livestream icon (project's local-on-demand) -->
                                <UIcon
                                    v-if="isOnDemand"
                                    name="local-on-demand"
                                    aria-hidden="true"
                                    class="shrink-0 w-[15px] h-[10px]"
                                    :class="videoIconClasses"
                                />
                            </div>

                            <!-- Date/time line -->
                            <p
                                v-if="formattedDateTime && !isArchive"
                                class="font-hubspot-sans font-normal text-[14px] leading-[1.5] tracking-[0.21px] w-full"
                                :class="timeClasses"
                            >{{ formattedDateTime }}</p>
                        </div>
                    </div>

                    <!-- Title — clicking navigates to session page (opens the My Event modal instead when isMyEvent) -->
                    <component :is="isMyEvent ? 'div' : NuxtLink" :to="isMyEvent ? undefined : sessionLink" class="w-full no-underline">
                    <p
                        :class="['font-hubspot-serif font-normal text-[30px] leading-[1.3] tracking-normal w-full', hoverUnderlineCls, titleClasses]"
                    >{{ session.title }}</p>
                    </component>
                </div>

                <!-- Description — clicking navigates to session page (opens the My Event modal instead when isMyEvent) -->
                <component :is="isMyEvent ? 'div' : NuxtLink" v-if="session.short_description || session.description" :to="isMyEvent ? undefined : sessionLink" class="w-full no-underline">
                <div
                    :class="['font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] w-full', hoverUnderlineCls, titleClasses]"
                    v-html="session.short_description || session.description"
                ></div>
                </component>

                <!-- AI recommendation callout — only the Find a Session
                     suggestion card passes `recommendation`; agenda cards leave
                     it empty and render nothing. Dark-pink outline + sparkle
                     glyph, laid out as a flex row so the reason text never
                     carries placeholder leading spaces. -->
                <div
                    v-if="recommendation"
                    data-test="reason-callout"
                    :class="['border rounded-[10px] px-[19px] py-[17px] flex items-start gap-[10px] w-full', reasonBoxClasses]"
                >
                    <svg :class="['shrink-0 mt-[2px] size-[15px]', reasonTextClasses]" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                        <circle cx="7.5" cy="7.5" r="7.5" fill="currentColor" />
                        <path d="M11.5263 8.46995C11.5263 8.54151 11.4683 8.59954 11.3973 8.59954C10.264 8.59954 8.92285 9.94044 8.92285 11.0734C8.92285 11.145 8.86481 11.203 8.7938 11.203C8.7228 11.203 8.66476 11.145 8.66476 11.0734C8.66419 9.94044 7.32302 8.59954 6.18978 8.59954C6.11821 8.59954 6.06073 8.54151 6.06073 8.46995C6.06073 8.3984 6.11877 8.34037 6.18978 8.34037C7.32302 8.34037 8.66419 6.99947 8.66419 5.86703C8.66419 5.79547 8.72224 5.73744 8.79324 5.73744C8.86424 5.73744 8.92229 5.79547 8.92229 5.86703C8.92229 6.99947 10.2635 8.34037 11.3967 8.34037C11.4683 8.34037 11.5258 8.3984 11.5258 8.46995H11.5263Z" :fill="reasonSparkleFill" />
                        <path d="M7.2753 5.73726C7.2753 5.78496 7.23661 5.82364 7.18927 5.82364C6.43378 5.82364 5.53966 6.71758 5.53966 7.47292C5.53966 7.52062 5.50096 7.55931 5.45363 7.55931C5.40629 7.55931 5.3676 7.52062 5.3676 7.47292C5.36722 6.71758 4.4731 5.82364 3.71761 5.82364C3.6699 5.82364 3.63158 5.78496 3.63158 5.73726C3.63158 5.68955 3.67027 5.65087 3.71761 5.65087C4.4731 5.65087 5.36722 4.75693 5.36722 4.00197C5.36722 3.95427 5.40592 3.91558 5.45325 3.91558C5.50059 3.91558 5.53928 3.95427 5.53928 4.00197C5.53928 4.75693 6.4334 5.65087 7.1889 5.65087C7.23661 5.65087 7.27493 5.68955 7.27493 5.73726H7.2753Z" :fill="reasonSparkleFill" />
                    </svg>
                    <p :class="['flex-1 font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px] m-0', reasonTextClasses]">{{ recommendation }}</p>
                </div>

                <!-- Speaker avatars (skip for user-event) — each links to the speaker page.
                     The wrapper is the container the collapsed row measures itself
                     against (see SPEAKER_FACE_FIT). -->
                <div
                    v-if="variant !== 'user-event' && speakers.length > 0"
                    class="@container/speakers w-full"
                >
                <div class="flex items-center isolate agenda-speaker-row">
                    <nuxt-link
                        v-for="(speaker, i) in shownSpeakers"
                        :key="speaker.id || speaker.name || i"
                        :to="`/speakers/${speaker.slug || speaker.id}`"
                        :aria-label="speaker.name || 'View speaker'"
                        :style="{
                            // Every hexagon needs its own layer, and the chip
                            // sits under the lot of them.
                            zIndex: shownSpeakers.length - i + 1,
                        }"
                        :class="[
                            i < shownSpeakers.length - 1 || collapseSpeakers ? 'mr-[-10px]' : '',
                            collapseSpeakers && SPEAKER_FACE_FIT[i] ? SPEAKER_FACE_FIT[i] : 'display-block',
                        ]"
                        class="shrink-0 [&:hover_svg]:scale-110"
                        @click.stop
                    >
                    <svg
                        viewBox="0 0 68 68"
                        class="size-[64px] shrink-0 overflow-visible transition-transform duration-200"
                        role="img"
                        aria-hidden="true"
                    >
                        <defs>
                            <pattern
                                :id="`${cardId}-speaker-${i}`"
                                patternUnits="objectBoundingBox"
                                width="1"
                                height="1"
                            >
                                <image
                                    :href="speaker.profile_image_url || speaker.profile_image?.[0]?.url || ''"
                                    x="0" y="0" width="68" height="68"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </pattern>
                        </defs>
                        <path
                            :d="SPEAKER_HEX_PATH"
                            :fill="`url(#${cardId}-speaker-${i})`"
                            :stroke="speakerStrokeColor"
                            stroke-width="2"
                            stroke-linejoin="round"
                        />
                    </svg>
                    </nuxt-link>

                    <!-- "+N": the rest of the speakers, as one more hexagon. Opens the
                         session (the drawer, or the page in a new tab), where every
                         speaker is listed. -->
                    <component
                        v-if="collapseSpeakers"
                        :is="NuxtLink"
                        :to="sessionLink"
                        :aria-label="`View all ${speakers.length} speakers`"
                        class="relative shrink-0 display-block size-[64px] no-underline [&:hover_svg]:scale-110"
                        :style="{ zIndex: 1 }"
                        @click.stop
                    >
                        <svg viewBox="0 0 68 68" class="size-[64px] overflow-visible transition-transform duration-200" aria-hidden="true">
                            <path
                                :d="SPEAKER_HEX_PATH"
                                :fill="speakerChipFill"
                                :stroke="speakerStrokeColor"
                                stroke-width="2"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span class="absolute inset-0 flex items-center justify-center font-hubspot-sans font-normal text-[14px] leading-[23px] tracking-[0.28px] text-(--magenta)">
                            <span v-for="label in SPEAKER_CHIP_LABELS" :key="label.faces" :class="label.cls">+{{ speakers.length - label.faces }}</span>
                        </span>
                    </component>
                </div>
                </div>

                <!-- Sponsor logos -->
                <div
                    v-if="sponsorList.length > 0"
                    class="border-t border-[#dc95be] pt-[27px] flex flex-wrap gap-[12px] items-start w-full"
                >
                    <div
                        v-for="(sponsor, i) in sponsorList"
                        :key="sponsor.name || i"
                        class="bg-white rounded-[10px] w-[115px] h-[74px] overflow-clip flex items-center justify-center"
                    >
                        <img
                            v-if="sponsor.logoUrl"
                            :src="sponsor.logoUrl"
                            :alt="sponsor.name"
                            class="display-block max-w-full max-h-full object-contain p-[8px]"
                        />
                        <span
                            v-else
                            class="font-hubspot-sans text-[12px] text-(--magenta) text-center px-[8px]"
                        >{{ sponsor.name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Actions row (outside body padding, full-width). Buttons are gated
             solely by the global, time-released feature flags. -->
        <div v-if="!isMyEvent && (showMySchedule || (hasTwoButtons && showSeatReservations))" :class="actionsRowClasses">
            <!-- Add to My Schedule -->
            <button
                v-if="showMySchedule"
                :class="[addButtonClasses, (addButtonSaved && !delegateAdd) ? 'group/addbtn' : '']"
                :aria-label="addButtonSaved ? 'Added to My Schedule' : 'Add to My Schedule'"
                :disabled="busy || (delegateAdd && addButtonSaved)"
                @click.stop.prevent="onAddButton"
            >
                <!-- When added, hovering rotates the + into an X and swaps the
                     label to "Remove Session" — but only in the agenda's
                     own toggle mode; delegateAdd (suggestion card) has no
                     remove affordance, so it shows a static "Added" state. -->
                <Plus :class="['size-[11px] shrink-0 transition-transform duration-200 group-hover/addbtn:rotate-45 motion-reduce:transition-none', ICON_BAND]" />
                <span class="@max-[399px]:hidden">
                    <template v-if="addButtonSaved">
                        <span :class="!delegateAdd ? 'group-hover/addbtn:hidden' : ''">Added to My Schedule</span>
                        <span v-if="!delegateAdd" class="hidden group-hover/addbtn:inline">Remove Session</span>
                    </template>
                    <template v-else>Add to My Schedule</template>
                </span>
            </button>

            <!-- Save a Seat (limited-capacity and full-action-required only) -->
            <button
                v-if="hasTwoButtons && showSeatReservations"
                :class="['group/seatbtn', seatButtonClasses]"
                :aria-label="seatButtonWaitlisted ? 'Waitlisted' : seatButtonSaved ? 'Seat Saved' : (seatIsStandby ? 'Join Waitlist' : 'Save a Seat')"
                @click.stop.prevent="handleSeat"
            >
                <TimeCircle v-if="seatIsStandby" :class="['size-[14px] shrink-0', ICON_BAND]" />
                <Seat v-else :class="['w-[10.167px] h-[11.611px] shrink-0', ICON_BAND]" />
                <span v-if="seatButtonWaitlisted" class="@max-[399px]:hidden">
                    <span class="group-hover/seatbtn:hidden">Waitlisted</span>
                    <!-- The hover copy is width-dependent, because the full
                         wording does not fit beside "Added to My Schedule" on a
                         normal agenda card (UNB-753). Measured: the pair needs a
                         507.6px card to hold "Remove from Waitlist" with icons,
                         and the three-up grid gives 412px at 1357px viewport —
                         only the wide layouts (600px cards at 1920px) have room.
                         "Leave List" fits from 384.7px up, so it covers every
                         width where a label shows at all.
                         The container variant sits on the inner spans rather than
                         stacking onto group-hover, which keeps two independent
                         conditions independent. -->
                    <span class="hidden group-hover/seatbtn:inline">
                        <span class="@min-[508px]:hidden">Leave List</span>
                        <span class="hidden @min-[508px]:inline">Remove from Waitlist</span>
                    </span>
                </span>
                <span v-else class="@max-[399px]:hidden">{{ seatButtonSaved ? 'Seat Saved' : (seatIsStandby ? 'Join Waitlist' : 'Save a Seat') }}</span>
            </button>
        </div>
    </div>
</template>
